// Admin Jobs tab — CRUD interface for job listings.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Loader2, Link2, Globe } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import UrlGeneratorModal from "./UrlGeneratorModal";
import api from "../lib/api";

const CATEGORIES = [
  { key: "payroll", label: "Payroll" },
  { key: "accounting", label: "Accounting & Bookkeeping" },
  { key: "data", label: "Data & Analytics" },
  { key: "admin", label: "Admin & Executive Support" },
  { key: "hr", label: "HR & People Ops" },
  { key: "operations", label: "Operations & Records" },
];

function emptyJob() {
  return { title: "", category: "payroll", location: "Remote (US)", type: "Direct Hire · Remote", experience: "1–3 years", salary_range: "", software: "", description: "", active: true, remote: true };
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | job for edit
  const [urlGen, setUrlGen] = useState(null); // { mode, jobId?, jobTitle? }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/jobs");
      setJobs(r.data || []);
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Job deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const save = async (data) => {
    const payload = { ...data, software: data.software ? data.software.split(",").map((s) => s.trim()).filter(Boolean) : [] };
    try {
      if (data.id) await api.put(`/admin/jobs/${data.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(data.id ? "Job updated" : "Job created");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div data-testid="admin-jobs-tab">
      {/* Master Careers URL card */}
      <div className="mb-6 rounded-xl bg-gradient-to-br from-[#0F2A1F] to-[#163b2c] text-white p-6 flex items-center gap-4 flex-wrap" data-testid="master-url-card">
        <div className="h-12 w-12 rounded-md bg-[#D4AF37] text-[#0F2A1F] flex items-center justify-center flex-shrink-0">
          <Globe className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Master Careers URL</div>
          <div className="mt-1 text-lg font-bold">Post ONE link to advertise all {jobs.filter((j) => j.active !== false).length} jobs</div>
          <div className="mt-1 text-sm text-white/70">Ideal for a single ReadySetHire, LinkedIn, or Indeed master post covering all open roles.</div>
        </div>
        <button
          onClick={() => setUrlGen({ mode: "master", jobTitle: "All open roles" })}
          className="rounded-md bg-[#D4AF37] text-[#0F2A1F] px-5 py-2.5 text-sm font-bold hover:bg-[#e6c34c] inline-flex items-center gap-2"
          data-testid="master-url-btn"
        >
          <Link2 className="h-4 w-4" /> Get master URL
        </button>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-serif text-navy">Job Listings</h2>
          <p className="text-sm text-navy/55 mt-1">{jobs.length} total · {jobs.filter((j) => j.active !== false).length} active</p>
        </div>
        <button onClick={() => setEditing(emptyJob())} className="rounded-md bg-[#0F2A1F] text-white px-4 py-2 text-sm font-medium hover:bg-[#163b2c] inline-flex items-center gap-1.5" data-testid="admin-jobs-new-btn">
          <Plus className="h-4 w-4" /> New Job
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy/40 mx-auto" /></div>
      ) : (
        <div className="rounded-lg border border-navy/10 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-navy/[0.02] border-b border-navy/10">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Title</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Category</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Salary</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Active</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr><td colSpan="5" className="text-center py-8 text-navy/50">No jobs yet. Click &quot;New Job&quot; to add one.</td></tr>
              )}
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-navy/5 hover:bg-navy/[0.02]" data-testid={`job-row-${j.id}`}>
                  <td className="px-5 py-3 font-medium text-navy">{j.title}</td>
                  <td className="px-5 py-3 text-navy/70">{CATEGORIES.find((c) => c.key === j.category)?.label || j.category}</td>
                  <td className="px-5 py-3 text-navy/70">{j.salary_range}</td>
                  <td className="px-5 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${j.active === false ? "bg-navy/10 text-navy/55" : "bg-[#10B981]/15 text-[#10B981]"}`}>
                      {j.active === false ? "Draft" : "Live"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setUrlGen({ mode: "job", jobId: j.id, jobTitle: j.title })}
                        className="text-navy/60 hover:text-[#D4AF37] inline-flex items-center gap-1 text-xs font-semibold bg-[#D4AF37]/10 px-2.5 py-1.5 rounded-md ring-1 ring-[#D4AF37]/30"
                        data-testid={`get-url-${j.id}`}
                        title="Generate a job-board URL with tracking"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Get URL</span>
                      </button>
                      <button onClick={() => setEditing({ ...j, software: (j.software || []).join(", ") })} className="text-navy/60 hover:text-[#10B981]" data-testid={`edit-job-${j.id}`}><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => remove(j.id, j.title)} className="text-navy/60 hover:text-[#8b3a3a]" data-testid={`delete-job-${j.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <JobForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
      {urlGen && <UrlGeneratorModal {...urlGen} onClose={() => setUrlGen(null)} />}
    </div>
  );
}

function JobForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const isEdit = !!form.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.salary_range || !form.description) { toast.error("Title, salary and description are required"); return; }
    setBusy(true);
    try { await onSave(form); } finally { setBusy(false); }
  };

  const inputCls = "mt-2 bg-white border-navy/15 focus-visible:ring-[#10B981]";

  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="job-form-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-navy/10">
          <h2 className="font-serif text-xl text-navy">{isEdit ? "Edit job" : "New job"}</h2>
          <button onClick={onClose} className="text-navy/55 hover:text-navy" data-testid="job-form-close"><X className="h-5 w-5" /></button>
        </header>
        <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Title *</Label>
              <Input data-testid="job-form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>Category *</Label>
              <select className={`${inputCls} w-full rounded-md border px-3 py-2 text-sm`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="job-form-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Salary range *</Label>
              <Input data-testid="job-form-salary" className={inputCls} placeholder="e.g., $55,000 – $70,000" value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })} />
            </div>
            <div>
              <Label>Location</Label>
              <Input data-testid="job-form-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div>
              <Label>Experience</Label>
              <Input data-testid="job-form-experience" className={inputCls} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Software (comma-separated)</Label>
              <Input data-testid="job-form-software" className={inputCls} placeholder="e.g., ADP, Excel, QuickBooks" value={form.software} onChange={(e) => setForm({ ...form, software: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Description *</Label>
              <Textarea rows={5} data-testid="job-form-description" className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="job-form-active-label">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="job-form-active" />
              <span className="text-sm text-navy">Active (visible to candidates)</span>
            </label>
          </div>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-navy/10">
            <button type="button" onClick={onClose} className="text-sm text-navy/60 hover:text-navy px-4 py-2" data-testid="job-form-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="rounded-md bg-[#0F2A1F] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#163b2c] disabled:opacity-60" data-testid="job-form-save">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Create job")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
